import {
  CalendarRange,
  Swords,
  Target,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  DateStepper,
  EmptyState,
  LoadingCards,
  SectionLabel,
} from "./components";
import {
  formatCompactWeek,
  formatWeekLabel,
  isIsoDay,
  latestDate,
  nextDate,
  previousDate,
  weekStartOf,
} from "./dates";
import { MiniMarkdown } from "./MiniMarkdown";
import { riseStyle } from "./util";
import type { MarketWeeklyDigest } from "./types";

const SECTIONS: {
  field: "trends" | "top_opportunities" | "competitor_moves";
  icon: LucideIcon;
}[] = [
  { field: "trends", icon: TrendingUp },
  { field: "top_opportunities", icon: Target },
  { field: "competitor_moves", icon: Swords },
];

/** One digest per ISO week (week_start = Monday). */
export const WeeklyView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [locale = "es"] = useLocaleState();
  const [params, setParams] = useSearchParams();
  const weekParam = params.get("semana");

  const { data, isPending, error } = useGetList<MarketWeeklyDigest>(
    "market_weekly_digests",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "week_start", order: "DESC" },
    },
  );
  const weeks = useMemo(
    () => (data ?? []).map((digest) => digest.week_start),
    [data],
  );

  if (isPending) return <LoadingCards />;
  if (error)
    return (
      <EmptyState icon={CalendarRange} title={translate("market.error")} />
    );

  const latest = latestDate(weeks);
  if (!latest)
    return (
      <EmptyState
        icon={CalendarRange}
        title={translate("market.weekly.no_digests")}
        hint={translate("market.weekly.no_digests_hint")}
      />
    );

  const selected = isIsoDay(weekParam) ? weekStartOf(weekParam) : latest;
  const digest = data?.find((entry) => entry.week_start === selected);
  const older = previousDate(weeks, selected);
  const newer = nextDate(weeks, selected);
  const go = (week: string | undefined) => {
    if (week) setParams({ semana: week });
  };

  return (
    <section
      aria-labelledby="mi-weekly-title"
      className="space-y-3 md:space-y-4"
    >
      <div className="space-y-3">
        <DateStepper
          previousLabel={translate("market.nav.previous_week")}
          nextLabel={translate("market.nav.next_week")}
          onPrevious={older ? () => go(older) : undefined}
          onNext={newer ? () => go(newer) : undefined}
          onLatest={selected !== latest ? () => go(latest) : undefined}
          heading={
            isMobile
              ? {
                  id: "mi-weekly-title",
                  text: formatCompactWeek(selected, locale),
                  label: formatWeekLabel(selected, locale),
                }
              : undefined
          }
        />
        {isMobile ? null : (
          <div>
            <p className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">
              {translate("market.weekly.heading")}
            </p>
            <h2
              id="mi-weekly-title"
              className="text-xl font-semibold tracking-tight md:text-[1.375rem]"
            >
              {formatWeekLabel(selected, locale)}
            </h2>
          </div>
        )}
      </div>
      {digest ? (
        <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-3">
          {SECTIONS.map(({ field, icon }, index) => (
            <article
              key={field}
              className="mi-card mi-rise min-w-0 p-4 md:p-5"
              style={riseStyle(index)}
            >
              <SectionLabel icon={icon}>
                {translate(`market.weekly.${field}`)}
              </SectionLabel>
              {digest[field]?.trim() ? (
                <MiniMarkdown source={digest[field]} className="mt-1" />
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">
                  {translate("market.weekly.empty_section")}
                </p>
              )}
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={CalendarRange}
          title={translate("market.weekly.no_digest_for")}
          action={
            <Button
              type="button"
              className="btn-save rounded-full px-5"
              onClick={() => go(latest)}
            >
              {translate("market.weekly.go_latest")}
            </Button>
          }
        />
      )}
    </section>
  );
};
